import React, { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { FieldConfig } from 'models/fleet-management/FleetDataConfiguration';
import { FleetDataFilter, getFilterValueConfig } from 'models/fleet-management/FleetDataFilter';
import { useTranslation } from 'react-i18next';
import { FieldType, FilterType } from 'models/enums/fleet-management';
import { useAppSelector } from 'store';
import {
  selectFleetData,
  selectFleetFieldsConfiguration,
  selectFleetFiltersConfiguration,
} from 'store/fleet-management/fleet-management-slice-selectors';
import { AddIcon, DeleteIcon } from '@common/utils/icons/icons';
import AutoComplete from 'components/forms/AutoComplete';
import { getFlatFieldValues } from 'utils/fleet-filters';
import TextInput from '@common/components/Inputs/TextInput';
import OptionSwitchInput from '@common/components/Inputs/OptionSwitchInput';
import DateInput from '@common/components/Inputs/DateInput';
import { DateTime } from 'luxon';
import { dateToStringFormat } from '@common/utils/date';

const FilterInput: React.FC<{
  disabled?: boolean;
  value: FleetDataFilter | null;
  filterIndex: number;
  datasetId: string;
  fields: FieldConfig[];
  onFilterChange: (filter: FleetDataFilter, filterIndex: number, toDelete?: boolean) => void;
}> = ({ fields, datasetId, filterIndex, value, disabled, onFilterChange }) => {
  const { t } = useTranslation();

  const filterConfigs = useAppSelector((state) => selectFleetFiltersConfiguration(state));
  const fieldConfigs = useAppSelector((state) => selectFleetFieldsConfiguration(state));
  const liveData = useAppSelector((state) => selectFleetData(state)).find(
    (l) => l.id === datasetId,
  );

  const [fieldsAnchorEl, setFieldsAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [filterTypeAnchorEl, setFilterTypeAnchorEl] = useState<HTMLButtonElement | null>(null);

  const openFieldsMenuClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFieldsAnchorEl(event.currentTarget);
  };

  const closeFieldsMenuHandler = () => {
    setFieldsAnchorEl(null);
  };

  const openFilterTypeMenuClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterTypeAnchorEl(event.currentTarget);
  };

  const closeFilterTypeMenuHandler = () => {
    setFilterTypeAnchorEl(null);
  };

  const filterChangeHandler = (newValue: string) => {
    if (value != null) {
      onFilterChange(
        { ...value, field: newValue, type: FilterType.EXACT, values: [] },
        filterIndex,
      );
    } else {
      onFilterChange(
        { id: uuidv4(), field: newValue, type: FilterType.EXACT, values: [] },
        filterIndex,
      );
    }
    setFieldsAnchorEl(null);
  };

  const filterTypeChangeHandler = (newType: FilterType) => {
    if (value == null) return;
    setFilterTypeAnchorEl(null);
    onFilterChange({ ...value, type: newType, values: [] }, filterIndex);
  };

  const deleteFilterClickHandler = () => {
    if (value == null) return;
    onFilterChange(value, filterIndex, true);
  };

  const valueChangeHandler = (
    newValue: string | number,
    valueIndex: number,
    toDelete?: boolean,
  ) => {
    if (value == null) return null;
    const existingValues = [...(value?.values ?? [])];
    if (toDelete) {
      existingValues.splice(valueIndex, 1);
    } else if (existingValues.length <= valueIndex) {
      existingValues.push(newValue);
    } else {
      existingValues[valueIndex] = newValue;
    }
    onFilterChange({ ...value, values: existingValues }, filterIndex);
  };

  const trueFalseChangeHandler = (newValue: string) => {
    if (value == null) return null;
    onFilterChange({ ...value, values: [newValue] }, filterIndex);
  };

  const numberInputChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
    valueIndex: number,
    toDelete?: boolean,
  ) => {
    const newValue = +event.target.value;
    valueChangeHandler(newValue, valueIndex, toDelete);
  };

  const dateChangeHandler = (newDate: DateTime | null, valueIndex: number) => {
    if (value == null || newDate == null) return;

    let newValue: string | null = null;
    if (valueIndex === 0) {
      newValue = newDate.startOf('day').toISO();
    } else if (valueIndex === 1) {
      newValue = newDate.endOf('day').toISO();
    }

    const setValues = [...(value?.values ?? [])];

    if (newValue == null) return;

    if (setValues.length <= valueIndex) {
      setValues.push(newValue);
    } else {
      setValues[valueIndex] = newValue;
    }
    onFilterChange({ ...value, values: setValues }, filterIndex);
  };

  const translatedValue =
    t('fleet-management-field-' + value?.field) !== 'fleet-management-field-' + value?.field
      ? t('fleet-management-field-' + value?.field)
      : t('label-widget-field');

  const fieldChoices = fieldConfigs?.find((f) => f.fieldName === value?.field)?.choices;

  const inputConfigs = useMemo(() => {
    const fieldType = fieldConfigs?.find((f) => f.fieldName === value?.field)?.type;
    const filterType = value?.type;
    if (fieldType == null || filterType == null) return null;

    return getFilterValueConfig(filterType, fieldType);
  }, [fieldConfigs, value?.field, value?.type]);

  return (
    <React.Fragment>
      <Stack
        sx={(theme) => ({
          width: '100%',
          borderRadius: 1,
          backgroundColor: theme.palette.action.selected,
        })}
      >
        <Stack
          direction="row"
          alignItems="center"
          width="100%"
          spacing={2}
          p={2}
          pr={value == null || disabled ? 2 : 0}
        >
          <Button
            onClick={openFieldsMenuClickHandler}
            sx={(theme) => ({
              backgroundColor: '#FFF',
              boxShadow: 0,
              height: theme.spacing(5),
              '&.Mui-disabled': {
                color: disabled ? theme.palette.text.primary : undefined,
              },
            })}
            fullWidth
            disabled={disabled}
          >
            {translatedValue}
          </Button>
          <Button
            fullWidth
            onClick={openFilterTypeMenuClickHandler}
            sx={(theme) => ({
              backgroundColor: '#FFF',
              boxShadow: 0,
              height: theme.spacing(5),
              '&.Mui-disabled': {
                color: disabled ? theme.palette.text.primary : undefined,
              },
            })}
            disabled={value == null || disabled}
          >
            {value?.type != null ? value.type : FilterType.EXACT}
          </Button>
          {value != null && !disabled && (
            <IconButton color="error" onClick={deleteFilterClickHandler}>
              <DeleteIcon />
            </IconButton>
          )}
        </Stack>

        {inputConfigs != null && value?.field != null && (
          <Stack
            px={2}
            pb={2}
            direction={inputConfigs.inputs === 2 ? 'row' : undefined}
            spacing={1}
            alignItems="center"
          >
            {Array.from({
              length: Math.max((value?.values ?? []).length, 1, inputConfigs.inputs),
            }).map((_, valueIndex) => (
              <React.Fragment key={valueIndex}>
                <Box width="100%">
                  {disabled ? (
                    <Typography
                      sx={(theme) => ({
                        px: 2,
                        lineHeight: theme.spacing(5),
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: 1,
                        width: inputConfigs.type === FieldType.BOOL ? 'min-content' : undefined,
                        textTransform:
                          inputConfigs.type === FieldType.BOOL ? 'uppercase' : undefined,
                      })}
                    >
                      {inputConfigs.type === FieldType.BOOL
                        ? value?.values != null &&
                          value.values.length === 1 &&
                          value.values[0] === 'true'
                          ? t('label-true')
                          : t('label-false')
                        : inputConfigs.type === FieldType.DATE
                          ? value?.values != null && value.values.length >= valueIndex + 1
                            ? dateToStringFormat(value.values[valueIndex].toString(), true)
                            : ''
                          : value?.values != null && value.values.length >= valueIndex + 1
                            ? value.values[valueIndex].toString()
                            : ''}
                    </Typography>
                  ) : inputConfigs.type === FieldType.BOOL ? (
                    <OptionSwitchInput
                      options={[
                        {
                          value: 'true',
                          tooltip: t('label-true'),
                          label: <Typography>{t('label-true')}</Typography>,
                        },
                        {
                          value: 'false',
                          tooltip: t('label-false'),
                          label: <Typography>{t('label-false')}</Typography>,
                        },
                      ]}
                      value={
                        value?.values != null && value.values.length >= valueIndex + 1
                          ? value.values[valueIndex].toString()
                          : 'false'
                      }
                      onChange={trueFalseChangeHandler}
                    />
                  ) : inputConfigs.type === FieldType.STRING ? (
                    <AutoComplete
                      textFieldProps={{
                        sx: {
                          backgroundColor: '#FFF',
                          borderRadius: 1,
                          '& .MuiOutlinedInput-root': { paddingRight: '12px!important' },
                        },
                        placeholder: 'Value',
                      }}
                      size="small"
                      value={
                        value?.values != null && value.values.length >= valueIndex + 1
                          ? value.values[valueIndex].toString()
                          : ''
                      }
                      onChange={(newValue) => valueChangeHandler(newValue, valueIndex)}
                      options={
                        fieldChoices ??
                        (liveData?.data == null
                          ? []
                          : Array.from(new Set(getFlatFieldValues(liveData.data, value.field))))
                      }
                      endIcon={
                        inputConfigs.inputs === -1 ? <DeleteIcon color="error" /> : undefined
                      }
                      onEndIconClick={() => valueChangeHandler('', valueIndex, true)}
                    />
                  ) : inputConfigs.type === FieldType.NUMBER ? (
                    <TextInput
                      type="number"
                      size="small"
                      sx={(theme) => ({
                        backgroundColor: '#FFF',
                        borderRadius: 1,
                      })}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        numberInputChangeHandler(event, valueIndex)
                      }
                      value={
                        value?.values != null && value.values.length >= valueIndex + 1
                          ? value.values[valueIndex]
                          : ''
                      }
                    />
                  ) : inputConfigs.type === FieldType.DATE ? (
                    <DateInput
                      sx={{
                        backgroundColor: '#FFF',
                        height: 40,
                        '& .MuiInput-input': { height: 40, padding: 0, paddingLeft: 1 },
                        '& .MuiIconButton-root': { margin: 0 },
                      }}
                      borderRadius={1}
                      date={
                        value?.values != null && value.values.length >= valueIndex + 1
                          ? DateTime.fromISO(value.values[valueIndex].toString())
                          : null
                      }
                      onDateTimeChange={(newDate: DateTime<boolean> | null) =>
                        dateChangeHandler(newDate, valueIndex)
                      }
                      minDate={
                        valueIndex === 1 && value?.values != null && value.values.length > 0
                          ? DateTime.fromISO(value.values[0].toString())
                          : undefined
                      }
                      maxDate={
                        valueIndex === 0 && value?.values != null && value.values.length === 2
                          ? DateTime.fromISO(value.values[1].toString())
                          : undefined
                      }
                    />
                  ) : null}
                </Box>
                {inputConfigs.inputs === 2 && valueIndex === 0 && (
                  <Typography variant="subtitle1">{t('input-label-to')}</Typography>
                )}
                {!disabled &&
                  inputConfigs.inputs === -1 &&
                  (valueIndex === value.values.length - 1 || value.values.length === 0) && (
                    <Stack direction="row" width="100%" justifyContent="flex-end">
                      <Button
                        startIcon={<AddIcon />}
                        size="small"
                        onClick={() => valueChangeHandler('', value.values.length)}
                      >
                        {t('button-add')}
                      </Button>
                    </Stack>
                  )}
              </React.Fragment>
            ))}
          </Stack>
        )}
      </Stack>

      <Menu
        MenuListProps={{
          'aria-labelledby': 'fade-button',
        }}
        anchorEl={fieldsAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={fieldsAnchorEl != null}
        onClose={closeFieldsMenuHandler}
      >
        <Box
          sx={(theme) => ({
            width: theme.spacing(32),
            maxHeight: theme.spacing(24),
          })}
        >
          {fields.map((f) => (
            <MenuItem
              key={f.fieldName}
              value={f.fieldName}
              onClick={() => filterChangeHandler(f.fieldName)}
            >
              {t('fleet-management-field-' + f.fieldName)}
            </MenuItem>
          ))}
        </Box>
      </Menu>
      <Menu
        MenuListProps={{
          'aria-labelledby': 'fade-button',
        }}
        anchorEl={filterTypeAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={filterTypeAnchorEl != null}
        onClose={closeFilterTypeMenuHandler}
      >
        <Box
          sx={(theme) => ({
            width: theme.spacing(32),
            maxHeight: theme.spacing(24),
          })}
        >
          {(
            filterConfigs?.find(
              (fC) => fC.type === fieldConfigs?.find((f) => f.fieldName === value?.field)?.type,
            )?.filters ?? []
          ).map((f) => (
            <MenuItem key={f} value={f} onClick={() => filterTypeChangeHandler(f)}>
              {f}
            </MenuItem>
          ))}
        </Box>
      </Menu>
    </React.Fragment>
  );
};

export default FilterInput;
